/**
 * Reports an exception caught by the admin error boundary (`app/admin/error.tsx`)
 * so "Something went wrong" screens can be investigated after the fact (#886).
 *
 * Emits one structured `[AdminError]` console line and one Sentry event, tagged
 * with the route and the selected organisation. Only internal identifiers are
 * attached — no email, name or other personal data (`sendDefaultPii` is off).
 *
 * Must never throw: it runs inside the error boundary itself.
 */

import * as Sentry from '@sentry/nextjs';
import { supabase } from '@/lib/supabase/client';

export const ADMIN_ERROR_SURFACE = 'admin_error_boundary';

export interface AdminErrorContext {
  message: string;
  name: string;
  digest: string | null;
  pathname: string | null;
  search: string | null;
  organizationId: string | null;
  userId: string | null;
  occurredAt: string;
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

async function resolveUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

export async function buildAdminErrorContext(
  error: Error & { digest?: string },
  pathname: string | null,
): Promise<AdminErrorContext> {
  return {
    message: error?.message ?? String(error),
    name: error?.name ?? 'Error',
    digest: error?.digest ?? null,
    pathname,
    search: typeof window !== 'undefined' ? window.location.search || null : null,
    organizationId: safeLocalStorageGet('currentOrganizationId'),
    userId: await resolveUserId(),
    occurredAt: new Date().toISOString(),
  };
}

/**
 * Logs and reports the error. Resolves to the Sentry event id (shown to the
 * user as a support reference) or `null` if reporting failed.
 */
export async function reportAdminError(
  error: Error & { digest?: string },
  pathname: string | null,
): Promise<string | null> {
  let context: AdminErrorContext | null = null;
  try {
    context = await buildAdminErrorContext(error, pathname);
  } catch {
    context = null;
  }

  try {
    console.error('[AdminError]', JSON.stringify(context), error);
  } catch {
    // Logging must not break the error boundary.
  }

  try {
    return Sentry.captureException(error, {
      tags: {
        surface: ADMIN_ERROR_SURFACE,
        pathname: context?.pathname ?? 'unknown',
        organization_id: context?.organizationId ?? 'none',
      },
      user: context?.userId ? { id: context.userId } : undefined,
      contexts: { admin_error: context ? { ...context } : {} },
    });
  } catch {
    return null;
  }
}
